import { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Modal, Select, Input, Form, Typography, Tag, Space, Checkbox, message as antdMessage } from "antd";
import styled from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import tokens from "../../theme/tokens";
import {
  createGoldStandardFromDocument,
  fetchCategories,
  selectCategorizationCategories,
  selectCategorizationSubtypes,
  selectCategoriesStatus,
  recategorizeDocument,
} from "../../slice/CategorizationSlice";
import {
  fetchMatterDetail,
  optimisticCategoryMove,
  reconcileCategoryMove,
  restoreCategoryMoveSnapshot,
} from "../../slice/MatterDetailsSlice";
import { buildCategoryOptions } from "./NegativeExampleModal";
import SubtypeSelect from "./SubtypeSelect";
import useAuth from "../../hooks/useAuth";

const { TextArea } = Input;
const { Text } = Typography;

const STATUS_IDLE = "idle";

const MODE_GOLD_STANDARD = "gold-standard";
const MODE_RECATEGORIZE = "recategorize";

const ReasonHintRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-top: 4px;
  color: ${tokens.text.tertiary};
  font-size: 12px;
  font-family: ${tokens.typography.fontFamily};
`;

const InferredBadge = styled(Tag)`
  font-size: 12px;
  border-radius: 4px;
`;

const ExistingGoldNotice = styled.div`
  padding: 8px 12px;
  border-radius: 6px;
  background: ${tokens.surface.page};
  border: 1px solid ${tokens.status?.open?.border || tokens.border.light};
  color: ${tokens.text.primary};
  font-size: 13px;
  margin-bottom: 12px;
`;

const REASON_MAX = 600;

const CategorizeDocumentModal = ({
  open,
  onClose,
  mode = MODE_GOLD_STANDARD,
  documentId,
  documentName = "",
  inferredCategory = "",
  existingGoldStandard = null,
  matterId = null,
}) => {
  const dispatch = useDispatch();
  const categories = useSelector(selectCategorizationCategories);
  const subtypes = useSelector(selectCategorizationSubtypes);
  const categoriesStatus = useSelector(selectCategoriesStatus);
  const {
    documentDetail,
    matterDetailsList,
    matterDetailsTree,
    totalRecords,
    updateMeta,
  } = useSelector((state) => state.matterDetails || {});
  const { currentFolderId } = useSelector((state) => state.websocket || {});

  const isRecategorize = mode === MODE_RECATEGORIZE;
  const { hasCapability } = useAuth();
  const canUseGoldStandard = hasCapability("ai.gold_standard");

  const [pickedCategory, setPickedCategory] = useState("");
  const [pickedSubtype, setPickedSubtype] = useState("");
  const [reason, setReason] = useState("");
  const [addNegative, setAddNegative] = useState(false);
  const [touched, setTouched] = useState({ category: false, reason: false });
  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const options = useMemo(() => buildCategoryOptions(categories), [categories]);

  useEffect(() => {
    if (!open) return;
    setPickedCategory(inferredCategory || "");
    setPickedSubtype("");
    setReason("");
    setAddNegative(false);
    setTouched({ category: false, reason: false });
    setSaving(false);
    setSubmitError("");
  }, [open, inferredCategory]);

  useEffect(() => {
    if (!open) return;
    if (categoriesStatus === STATUS_IDLE) {
      dispatch(fetchCategories());
    }
  }, [dispatch, open, categoriesStatus]);

  const categoryValid = !!pickedCategory.trim();
  const reasonLength = reason.trim().length;
  const reasonValid = reasonLength <= REASON_MAX;

  // The document's current (pre-move) category. Only when there IS one is
  // there something for the AI to have gotten "wrong", so the negative-example
  // opt-in is hidden otherwise. The backend independently recomputes the old
  // category server-side and only records a negative example if it actually
  // changes, so this is purely a UI gate.
  const currentCategory = (inferredCategory || "").trim();
  // Negative-example capture is Gold Standard training signal — Matthew
  // only (2026-08-05), same as the library itself and enforced again
  // server-side (documents/views/document.py::DocumentRecategorizeView).
  const canFlagNegative =
    isRecategorize && !!currentCategory && canUseGoldStandard;

  // In recategorize mode the reason note belongs to the negative-example
  // opt-in (it's the "why was the AI wrong" note), so only surface it once the
  // toggle is on. In gold-standard mode it is always shown. Optional either way.
  const showReason = !isRecategorize || (isRecategorize && addNegative);

  const alreadyGoldForPicked =
    !isRecategorize &&
    !!existingGoldStandard &&
    (existingGoldStandard.category || "").trim().toLowerCase() ===
      pickedCategory.trim().toLowerCase();

  const canSubmit = categoryValid && reasonValid && !saving;

  const handleSubmit = useCallback(async () => {
    setSubmitError("");

    if (!categoryValid) {
      setTouched((t) => ({ ...t, category: true }));
      return;
    }
    if (!reasonValid) {
      setTouched((t) => ({ ...t, reason: true }));
      return;
    }

    if (isRecategorize) {
      setSaving(true);
      const targetCategory = pickedCategory.trim();
      const snapshot = JSON.parse(
        JSON.stringify({
          matterDetailsTree,
          matterDetailsList,
          updateMeta,
          documentDetail,
          totalRecords,
        })
      );
      const optimisticDocument = {
        ...(updateMeta || documentDetail || {}),
        id: documentId,
        unique_id: String(documentId).startsWith("D")
          ? String(documentId)
          : `D${documentId}`,
        name: documentName,
        category: currentCategory,
        doc_category: currentCategory,
        matter: matterId || matterDetailsTree?.id,
      };
      dispatch(
        optimisticCategoryMove({
          documentId: String(documentId),
          category: targetCategory,
          document: optimisticDocument,
          currentFolderId,
        })
      );
      try {
        const flagNegative = canFlagNegative && addNegative;
        const recategorizeArgs = {
          documentId: String(documentId),
          category: targetCategory,
        };
        if (flagNegative) {
          recategorizeArgs.addNegative = true;
          const trimmedReason = reason.trim();
          if (trimmedReason) recategorizeArgs.reason = trimmedReason;
        }
        const action = await dispatch(recategorizeDocument(recategorizeArgs));
        if (recategorizeDocument.fulfilled.match(action)) {
          dispatch(
            reconcileCategoryMove({
              documentId: String(documentId),
              category: action?.payload?.doc_category || targetCategory,
              document: action?.payload?.document || action?.payload,
              destinationFolder: action?.payload?.destination_folder,
              currentFolderId,
            })
          );
          if (action?.payload?.negative_example_created) {
            const oldCategory =
              action?.payload?.previous_category || currentCategory;
            antdMessage.success(
              `Recategorized — flagged as a negative example for ${oldCategory}`
            );
          } else {
            antdMessage.success(`Document moved to ${pickedCategory}`);
          }
          onClose();
          if (matterId) {
            dispatch(fetchMatterDetail(matterId));
          }
        } else {
          dispatch(restoreCategoryMoveSnapshot(snapshot));
          const msg = action?.payload?.message || "Couldn't recategorize — try again.";
          antdMessage.error(msg);
          setSubmitError(msg);
          setSaving(false);
        }
      } catch {
        dispatch(restoreCategoryMoveSnapshot(snapshot));
        const msg = "Couldn't recategorize — try again.";
        antdMessage.error(msg);
        setSubmitError(msg);
        setSaving(false);
      }
      return;
    }

    // Gold-standard mode
    if (alreadyGoldForPicked) {
      antdMessage.info(`Already a gold standard in "${pickedCategory}".`);
      onClose();
      return;
    }

    setSaving(true);

    try {
      const action = await dispatch(
        createGoldStandardFromDocument({
          documentId: String(documentId),
          category: pickedCategory.trim(),
          subtype: pickedSubtype || undefined,
          notes: reason.trim() || undefined,
        })
      );
      if (createGoldStandardFromDocument.fulfilled.match(action)) {
        antdMessage.success(`Saved to gold standard library: ${pickedCategory}`);
        onClose();
      } else {
        const payload = action?.payload;
        if (payload?.status === 409) {
          antdMessage.info(`Already a gold standard in "${pickedCategory}".`);
          onClose();
        } else {
          const msg = payload?.message || "Couldn't save — try again.";
          antdMessage.error(msg);
          setSubmitError(msg);
          setSaving(false);
        }
      }
    } catch {
      const msg = "Couldn't save — try again.";
      antdMessage.error(msg);
      setSubmitError(msg);
      setSaving(false);
    }
  }, [
    dispatch,
    documentId,
    pickedCategory,
    pickedSubtype,
    reason,
    isRecategorize,
    categoryValid,
    reasonValid,
    addNegative,
    canFlagNegative,
    currentCategory,
    alreadyGoldForPicked,
    onClose,
    matterId,
    matterDetailsTree,
    matterDetailsList,
    updateMeta,
    documentDetail,
    totalRecords,
    currentFolderId,
  ]);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter" && !e.shiftKey && canSubmit) {
        e.preventDefault();
        handleSubmit();
      }
    },
    [canSubmit, handleSubmit]
  );

  const existingGoldCategory = existingGoldStandard?.category;

  const title = isRecategorize
    ? "Categorize document"
    : "Add to Gold Standard Library";

  const okLabel = isRecategorize
    ? saving
      ? "Moving…"
      : "Move to selected category"
    : saving
    ? "Saving…"
    : "Save to library";

  return (
    <Modal
      open={open}
      title={title}
      onCancel={saving ? undefined : onClose}
      onOk={handleSubmit}
      okText={okLabel}
      cancelText="Cancel"
      okButtonProps={{
        disabled: !canSubmit,
        "data-testid": "categorize-submit",
      }}
      cancelButtonProps={{ disabled: saving }}
      maskClosable={!saving}
      destroyOnHidden
      data-testid="categorize-document-modal"
    >
      <div onKeyDown={handleKeyDown}>
        <Form layout="vertical">
          <Form.Item label="Document" style={{ marginBottom: 8 }}>
            <Space>
              <Text strong>{documentName || "—"}</Text>
              {inferredCategory ? (
                <InferredBadge color="blue">{inferredCategory}</InferredBadge>
              ) : (
                <InferredBadge color="default" data-testid="no-category-pill">
                  No category assigned
                </InferredBadge>
              )}
            </Space>
          </Form.Item>

          {isRecategorize && (
            <Text
              type="secondary"
              style={{ display: "block", marginBottom: 12, fontSize: 13 }}
              data-testid="recategorize-hint"
            >
              Move this document to the correct category.
            </Text>
          )}

          {!isRecategorize && existingGoldCategory && (
            <ExistingGoldNotice data-testid="existing-gold-notice">
              Already a gold standard in: <strong>{existingGoldCategory}</strong>
            </ExistingGoldNotice>
          )}

          <Form.Item
            label="Category"
            required
            validateStatus={touched.category && !categoryValid ? "error" : ""}
            help={
              touched.category && !categoryValid
                ? "Pick or type a category."
                : null
            }
          >
            <Select
              placeholder="Select or type a category"
              value={pickedCategory || undefined}
              onChange={(value) => {
                setPickedCategory(value);
                setTouched((t) => ({ ...t, category: true }));
              }}
              disabled={saving}
              showSearch
              optionFilterProp="label"
              options={options}
              data-testid="categorize-category-select"
              autoFocus
            />
          </Form.Item>

          {/* Recategorize moves a document between folders; a subtype
              does neither, so it only belongs on the gold-standard path. */}
          {!isRecategorize && (
            <SubtypeSelect
              subtypes={subtypes}
              value={pickedSubtype}
              category={pickedCategory}
              disabled={saving}
              onChange={setPickedSubtype}
              testId="categorize-subtype-select"
            />
          )}

          {canFlagNegative && (
            <Form.Item style={{ marginBottom: showReason ? 8 : 0 }}>
              <Checkbox
                checked={addNegative}
                disabled={saving}
                onChange={(e) => setAddNegative(e.target.checked)}
                data-testid="categorize-add-negative"
              >
                Also flag this as a negative example for the current category (
                <strong>{currentCategory}</strong>)
              </Checkbox>
            </Form.Item>
          )}

          {showReason && (
            <Form.Item
              label={
                isRecategorize
                  ? "Why was the AI wrong? (optional)"
                  : "Why is this the right category? (optional)"
              }
              validateStatus={
                touched.reason && !reasonValid ? "error" : ""
              }
              help={
                touched.reason && !reasonValid
                  ? `Keep it under ${REASON_MAX} characters.`
                  : null
              }
            >
              <TextArea
                rows={3}
                value={reason}
                onChange={(e) => {
                  setReason(e.target.value);
                  setTouched((t) => ({ ...t, reason: true }));
                }}
                disabled={saving}
                placeholder={
                  isRecategorize
                    ? "Optional — explain what the AI got wrong."
                    : "Optional — explain why this is a good example."
                }
                maxLength={REASON_MAX}
                data-testid="categorize-reason"
              />
              <ReasonHintRow>
                <span />
                <span>
                  {reasonLength}/{REASON_MAX}
                </span>
              </ReasonHintRow>
            </Form.Item>
          )}

          {submitError && (
            <Text type="danger" data-testid="categorize-submit-error">
              {submitError}
            </Text>
          )}
        </Form>
      </div>
    </Modal>
  );
};

CategorizeDocumentModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  mode: PropTypes.oneOf([MODE_GOLD_STANDARD, MODE_RECATEGORIZE]),
  documentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  documentName: PropTypes.string,
  inferredCategory: PropTypes.string,
  existingGoldStandard: PropTypes.shape({
    id: PropTypes.number,
    category: PropTypes.string,
  }),
  matterId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default CategorizeDocumentModal;
